// CustomBreadcrumbs.tsx
import React from "react";
import { Breadcrumbs, Link, Typography } from "@mui/material";
import { useLocation, Link as WouterLink } from "wouter";

interface Props {
  user?: { userType?: string };
}

// Map of path segment OR full path to label
const labelMap: Record<string, string> = {
  seller: "Home",
  property: "Property",
  add: "Add",
  edit: "Edit",
  list: "List",

  // full path overrides
  "/property/add": "Add Property",
  "/property/edit": "Edit Property",
};

// detect if path looks like an id (mongo ObjectId, UUID, numeric, etc.)
const isIdLike = (segment: string) =>
  /^[0-9a-fA-F]{24}$/.test(segment) || // Mongo ObjectId
  /^[0-9]+$/.test(segment) || // numbers
  /^[0-9a-fA-F-]{8,}$/.test(segment); // UUID-ish

const CustomBreadcrumbs: React.FC<Props> = ({ user }) => {
  const [location] = useLocation();

  // Hide breadcrumbs on these routes
  const hiddenRoutes = [
    "/",
    "/commercial",
    "/new-homes",
    "/rent",
    "/share",
    "/sold",
    "/login",
    "/register",
    "/reset-password",
  ];
  if (hiddenRoutes.includes(location)) {
    return null;
  }

  let pathnames = location.split("/").filter((x) => x);

  // Special rule: if seller, replace "seller" with "home"
  if (user?.userType === "seller" && pathnames[0] === "seller") {
    pathnames[0] = "home";
  }
  if (user?.userType === "buyer" && pathnames[0] === "buyer") {
    pathnames[0] = "home";
  }
  if (user?.userType === "admin" && pathnames[0] === "admin") {
    pathnames[0] = "home";
  }

  // filter out IDs/dynamic params
  pathnames = pathnames.filter((seg) => !isIdLike(seg));

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-2">
      <Breadcrumbs aria-label="breadcrumb">
        {/* Always show Home link */}
        <Link
          component={WouterLink}
          to={user?.userType === "seller" ? "/" : "/"}
          underline="hover"
          color="inherit"
        >
          Home
        </Link>

        {pathnames.map((value, index) => {
          const to = "/" + pathnames.slice(0, index + 1).join("/");
          const isLast = index === pathnames.length - 1;

          const label =
            labelMap[to] ||
            labelMap[value] ||
            value.charAt(0).toUpperCase() + value.slice(1);

          // skip duplicate "Home"
          if (label.toLowerCase() === "home" && index === 0) return null;

          return isLast ? (
            <Typography key={to} color="text.primary">
              {label}
            </Typography>
          ) : (
            <Link
              key={to}
              component={WouterLink}
              to={to}
              underline="hover"
              color="inherit"
            >
              {label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </section>
  );
};

export default CustomBreadcrumbs;
